import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from './Login';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const formaConfig = {
  pix:           { label: 'PIX',           icon: '⚡' },
  dinheiro:      { label: 'Dinheiro',      icon: '💵' },
  transferencia: { label: 'Transferência', icon: '🏦' },
  cartao:        { label: 'Cartão',        icon: '💳' },
};

const fmt = (v) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── STYLES ───────────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 48px;
  padding-bottom: 6rem;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  padding: 1.2rem 1.25rem 2.5rem;
  color: white;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
  h1 { margin: 0; font-size: 1.1rem; font-weight: 700; }
`;

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  opacity: 0.9;
  button {
    background: rgba(255,255,255,0.2);
    border: none; color: white; border-radius: 50%;
    width: 26px; height: 26px; cursor: pointer; font-size: 0.9rem;
    display: flex; align-items: center; justify-content: center;
  }
`;

// ─── META / GAMIFICAÇÃO ───────────────────────────────────────────────────────
const GoalCard = styled(motion.div)`
  background: white;
  border-radius: 1rem;
  padding: 1rem 1.25rem;
  margin: -1.4rem 1.25rem 0.75rem;
  box-shadow: 0 4px 20px rgba(51,65,85,0.12);
  position: relative;
  z-index: 2;
`;

const GoalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
  .title { font-size: 0.78rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; }
  .pct   { font-size: 0.9rem; font-weight: 700; color: #334155; }
`;

const ProgressBar = styled.div`
  background: #f1f5f9;
  border-radius: 20px;
  height: 10px;
  overflow: hidden;
  div {
    height: 100%;
    border-radius: 20px;
    background: linear-gradient(90deg, #334155, #475569);
    width: ${p => Math.min(p.pct, 100)}%;
    transition: width 0.8s ease;
  }
`;

const GoalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #6b7280;
  span b { color: #1f1f1f; }
`;

const GoalInput = styled.div`
  display: flex; gap: 0.5rem; margin-top: 0.75rem;
  input {
    flex: 1; padding: 0.45rem 0.7rem;
    border: 1.5px solid #e5e7eb; border-radius: 0.5rem; font-size: 0.85rem;
    &:focus { border-color: #334155; outline: none; }
  }
  button {
    background: #334155; color: white; border: none;
    border-radius: 0.5rem; padding: 0.45rem 0.9rem;
    font-size: 0.8rem; font-weight: 600; cursor: pointer;
  }
`;

// ─── BADGES / STREAK ─────────────────────────────────────────────────────────
const BadgesRow = styled.div`
  display: flex; gap: 0.6rem; padding: 0 1.25rem; margin-bottom: 0.75rem; overflow-x: auto;
  &::-webkit-scrollbar { display: none; }
`;

const Badge = styled(motion.div)`
  background: white;
  border-radius: 0.75rem;
  padding: 0.7rem 1rem;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
  text-align: center; white-space: nowrap; flex-shrink: 0;
  .icon { font-size: 1.3rem; display: block; }
  .value { font-size: 1rem; font-weight: 700; color: #1f1f1f; }
  .label { font-size: 0.65rem; color: #9ca3af; margin-top: 0.1rem; }
`;

// ─── STATS GRID ───────────────────────────────────────────────────────────────
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;
  padding: 0 1.25rem;
  margin-bottom: 0.75rem;
`;

const StatCard = styled(motion.div)`
  background: white;
  border-radius: 1rem;
  padding: 0.9rem 1rem;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
  .icon { font-size: 1.3rem; display: block; margin-bottom: 0.3rem; }
  .value { font-size: 1.05rem; font-weight: 700; color: ${p => p.color || '#1f1f1f'}; }
  .label { font-size: 0.7rem; color: #9ca3af; margin-top: 0.15rem; }
  .sub   { font-size: 0.68rem; color: #6b7280; margin-top: 0.2rem; }
`;

const VariationTag = styled.span`
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.1rem 0.4rem;
  border-radius: 20px;
  background: ${p => p.up ? '#d1fae5' : '#fee2e2'};
  color: ${p => p.up ? '#059669' : '#dc2626'};
  margin-left: 0.3rem;
`;

// ─── PROJEÇÃO ─────────────────────────────────────────────────────────────────
const ProjecaoCard = styled.div`
  background: linear-gradient(135deg, #f1f5f9, #f8fafc);
  border-radius: 1rem;
  padding: 0.9rem 1.25rem;
  margin: 0 1.25rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  .icon { font-size: 1.4rem; }
  .text { flex: 1; }
  .title { font-size: 0.72rem; font-weight: 700; color: #334155; text-transform: uppercase; letter-spacing: 0.04em; }
  .value { font-size: 1rem; font-weight: 700; color: #1f1f1f; }
  .hint  { font-size: 0.7rem; color: #6b7280; margin-top: 0.1rem; }
`;

// ─── TRANSAÇÕES ───────────────────────────────────────────────────────────────
const Section = styled.div`
  padding: 0 1.25rem;
  margin-bottom: 0.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.6rem;
  h2 { font-size: 0.85rem; font-weight: 700; color: #374151; margin: 0; }
`;

const FilterTabs = styled.div`
  display: flex; gap: 0.4rem;
`;

const Tab = styled.button`
  background: ${p => p.active ? '#334155' : '#f3f4f6'};
  color: ${p => p.active ? 'white' : '#6b7280'};
  border: none; border-radius: 20px;
  padding: 0.3rem 0.75rem; font-size: 0.72rem; font-weight: 600; cursor: pointer;
`;

const TxCard = styled(motion.div)`
  background: white;
  border-radius: 0.85rem;
  padding: 0.85rem 1rem;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const TxIcon = styled.div`
  width: 38px; height: 38px;
  border-radius: 0.6rem;
  background: ${p => p.bg};
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem; flex-shrink: 0;
`;

const TxInfo = styled.div`
  flex: 1; min-width: 0;
  .desc { font-size: 0.85rem; font-weight: 600; color: #1f1f1f; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .meta { font-size: 0.7rem; color: #9ca3af; margin-top: 0.1rem; }
`;

const TxRight = styled.div`
  text-align: right; flex-shrink: 0;
  .amount { font-size: 0.9rem; font-weight: 700; color: ${p => p.type === 'despesa' ? '#dc2626' : '#059669'}; }
  .status { font-size: 0.65rem; margin-top: 0.2rem; }
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${p => p.status === 'recebido' ? '#10b981' : p.status === 'pendente' ? '#f59e0b' : '#9ca3af'};
  margin-right: 0.25rem;
  vertical-align: middle;
`;

const ConfirmBtn = styled.button`
  background: #f1f5f9; color: #334155; border: none;
  border-radius: 20px; padding: 0.2rem 0.6rem;
  font-size: 0.65rem; font-weight: 700; cursor: pointer;
  margin-top: 0.25rem;
  &:hover { background: #334155; color: white; }
`;

const AddBtn = styled(motion.button)`
  position: fixed;
  bottom: 5.5rem; right: 1.25rem;
  width: 52px; height: 52px;
  background: #334155; color: white;
  border: none; border-radius: 50%;
  font-size: 1.5rem; cursor: pointer;
  box-shadow: 0 4px 16px rgba(51,65,85,0.35);
  display: flex; align-items: center; justify-content: center;
  z-index: 100;
`;

const Empty = styled.div`
  text-align: center; padding: 2rem 1rem; color: #9ca3af;
  .icon { font-size: 2rem; display: block; margin-bottom: 0.5rem; }
  p { font-size: 0.82rem; margin: 0; }
`;

// ─── OVERLAY CONFIRMAR PAGAMENTO ──────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 300;
  display: flex; align-items: flex-end; justify-content: center;
`;
const Sheet = styled(motion.div)`
  background: #fff; border-radius: 1.2rem 1.2rem 0 0;
  padding: 1.5rem; width: 100%; max-width: 480px;
  h3 { margin: 0 0 1rem; font-size: 1rem; color: #1f1f1f; }
  label { font-size: 0.78rem; color: #555; display: block; margin-bottom: 0.2rem; }
  input, select {
    width: 100%; padding: 0.6rem 0.8rem;
    border: 1.5px solid #e5e7eb; border-radius: 0.5rem;
    font-size: 0.9rem; margin-bottom: 0.8rem; box-sizing: border-box;
    &:focus { border-color: #334155; outline: none; }
  }
`;
const SheetRow = styled.div`display: flex; gap: 0.6rem;`;
const SheetSave = styled.button`
  background: #334155; color: white; border: none; border-radius: 0.6rem;
  padding: 0.75rem; font-size: 0.95rem; font-weight: 600; cursor: pointer; flex: 2;
  &:disabled { opacity: 0.6; }
`;
const SheetCancel = styled.button`
  background: #f3f4f6; color: #555; border: none; border-radius: 0.6rem;
  padding: 0.75rem; font-size: 0.9rem; cursor: pointer; flex: 1;
`;

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const Financeiro = () => {
  const { token } = useAuth();

  const now = new Date();
  const [viewYear, setViewYear]   = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('todos'); // todos | receita | despesa
  const [sheet, setSheet]         = useState(null);   // null | 'confirm' | 'despesa' | 'goal'
  const [sheetData, setSheetData] = useState({});
  const [goalInput, setGoalInput] = useState('');
  const [editingGoal, setEditingGoal] = useState(false);

  const authH = { Authorization: `Bearer ${token}` };
  const month = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;

  const loadSummary = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/transactions/summary?month=${month}`, { headers: authH });
      if (r.ok) {
        const data = await r.json();
        setSummary(data);
        setGoalInput(data.target ? String(data.target) : '');
      }
    } catch {}
    setLoading(false);
  }, [token, month]); // eslint-disable-line

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const saveGoal = async () => {
    if (!goalInput || isNaN(parseFloat(goalInput))) return;
    try {
      await fetch(`${API}/transactions/goal`, {
        method: 'PUT',
        headers: { ...authH, 'Content-Type': 'application/json' },
        body: JSON.stringify({ month, target: parseFloat(goalInput) })
      });
      setEditingGoal(false);
      loadSummary();
    } catch {}
  };

  const confirmPayment = async () => {
    const { id, paymentMethod } = sheetData;
    if (!paymentMethod) return;
    try {
      await fetch(`${API}/transactions/${id}`, {
        method: 'PATCH',
        headers: { ...authH, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'recebido', paymentMethod })
      });
      setSheet(null);
      loadSummary();
    } catch {}
  };

  const addDespesa = async () => {
    const { description, amount, category, date } = sheetData;
    if (!description || !amount) return;
    try {
      await fetch(`${API}/transactions`, {
        method: 'POST',
        headers: { ...authH, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'despesa', description,
          amount: parseFloat(amount),
          date: date || new Date().toISOString(),
          category: category || 'Despesa',
          status: 'recebido'
        })
      });
      setSheet(null);
      loadSummary();
    } catch {}
  };

  const transactions = (summary?.transactions || []).filter(t => {
    if (filter === 'todos') return t.status !== 'cancelado';
    return t.type === filter && t.status !== 'cancelado';
  });

  const pct = summary?.target > 0
    ? Math.round((summary.faturado / summary.target) * 100)
    : 0;

  const projecaoHint = () => {
    if (!summary) return '';
    const diff = (summary.target || 0) - (summary.projecao || 0);
    if (!summary.target) return 'Defina uma meta para ver a projeção';
    if (diff <= 0) return '🎉 No ritmo atual você bate sua meta!';
    const porDia = summary.faturado > 0
      ? summary.faturado / new Date().getDate()
      : 0;
    const diasNecessarios = porDia > 0 ? Math.ceil(diff / porDia) : null;
    return diasNecessarios
      ? `Faltam R$ ${fmt(diff)} — +${diasNecessarios} dia(s) de trabalho`
      : `Faltam R$ ${fmt(diff)} para bater a meta`;
  };

  const variacaoLabel = () => {
    if (summary?.variacao === null || summary?.variacao === undefined) return null;
    const v = summary.variacao;
    return { up: v >= 0, text: `${v >= 0 ? '+' : ''}${v}% vs mês ant.` };
  };

  return (
    <Page>
      {/* ── HEADER ── */}
      <Header>
        <HeaderTop>
          <h1>Financeiro</h1>
          {summary?.streak > 0 && (
            <span style={{ fontSize: '0.8rem', opacity: 0.9 }}>
              🔥 {summary.streak} dia{summary.streak > 1 ? 's' : ''} seguidos
            </span>
          )}
        </HeaderTop>
        <MonthNav>
          <button onClick={prevMonth}>‹</button>
          <span>{MESES[viewMonth]} {viewYear}</span>
          <button onClick={nextMonth}>›</button>
        </MonthNav>
      </Header>

      {/* ── META ── */}
      <GoalCard initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <GoalHeader>
          <span className="title">Meta do mês</span>
          <span className="pct">{summary?.target > 0 ? `${pct}%` : '—'}</span>
        </GoalHeader>
        <ProgressBar pct={pct}>
          <div />
        </ProgressBar>
        <GoalFooter>
          <span>Faturado: <b>R$ {fmt(summary?.faturado)}</b></span>
          {summary?.target > 0
            ? <span>Meta: <b>R$ {fmt(summary?.target)}</b></span>
            : <button
                onClick={() => setEditingGoal(true)}
                style={{ background: 'none', border: 'none', color: '#334155', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', padding: 0 }}
              >
                + Definir meta
              </button>
          }
        </GoalFooter>
        {(editingGoal || (summary?.target > 0 && editingGoal)) && (
          <GoalInput>
            <input
              type="number" placeholder="Meta em R$"
              value={goalInput}
              onChange={e => setGoalInput(e.target.value)}
            />
            <button onClick={saveGoal}>Salvar</button>
          </GoalInput>
        )}
        {summary?.target > 0 && !editingGoal && (
          <button
            onClick={() => setEditingGoal(true)}
            style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '0.7rem', cursor: 'pointer', marginTop: '0.4rem', padding: 0 }}
          >
            Alterar meta
          </button>
        )}
      </GoalCard>

      {/* ── STREAK / BADGES ── */}
      {!loading && (
        <BadgesRow>
          <Badge initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.05 }}>
            <span className="icon">💰</span>
            <div className="value">R$ {fmt(summary?.faturado)}</div>
            <div className="label">Recebido</div>
          </Badge>
          <Badge initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}>
            <span className="icon">⏳</span>
            <div className="value">R$ {fmt(summary?.pendente)}</div>
            <div className="label">A receber ({summary?.pendenteCnt || 0})</div>
          </Badge>
          <Badge initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 }}>
            <span className="icon">📉</span>
            <div className="value">R$ {fmt(summary?.despesas)}</div>
            <div className="label">Despesas</div>
          </Badge>
          <Badge initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}>
            <span className="icon">✨</span>
            <div className="value">R$ {fmt(summary?.lucro)}</div>
            <div className="label">Lucro líquido</div>
          </Badge>
        </BadgesRow>
      )}

      {/* ── STATS GRID (variação + projeção) ── */}
      {!loading && (
        <StatsGrid>
          <StatCard whileTap={{ scale: 0.97 }} color={summary?.variacao >= 0 ? '#059669' : '#dc2626'}>
            <span className="icon">📊</span>
            <div className="value">
              {summary?.variacao !== null && summary?.variacao !== undefined
                ? `${summary.variacao >= 0 ? '+' : ''}${summary.variacao}%`
                : '—'}
              {variacaoLabel() && (
                <VariationTag up={variacaoLabel().up}>
                  {variacaoLabel().up ? '↑' : '↓'}
                </VariationTag>
              )}
            </div>
            <div className="label">vs mês anterior</div>
            <div className="sub">Mês ant.: R$ {fmt(summary?.faturadoPrev)}</div>
          </StatCard>
          <StatCard whileTap={{ scale: 0.97 }} color="#334155">
            <span className="icon">🔮</span>
            <div className="value">R$ {fmt(summary?.projecao)}</div>
            <div className="label">Projeção do mês</div>
            <div className="sub">no ritmo atual</div>
          </StatCard>
        </StatsGrid>
      )}

      {/* ── PROJEÇÃO HINT ── */}
      {!loading && summary && (
        <ProjecaoCard>
          <div className="icon">🎯</div>
          <div className="text">
            <div className="title">Projeção</div>
            <div className="value">R$ {fmt(summary.projecao)}</div>
            <div className="hint">{projecaoHint()}</div>
          </div>
        </ProjecaoCard>
      )}

      {/* ── LISTA TRANSAÇÕES ── */}
      <Section>
        <SectionHeader>
          <h2>Movimentações</h2>
          <FilterTabs>
            <Tab active={filter === 'todos'}   onClick={() => setFilter('todos')}>Todas</Tab>
            <Tab active={filter === 'receita'} onClick={() => setFilter('receita')}>Receitas</Tab>
            <Tab active={filter === 'despesa'} onClick={() => setFilter('despesa')}>Despesas</Tab>
          </FilterTabs>
        </SectionHeader>

        {loading ? (
          <Empty><span className="icon">⏳</span><p>Carregando...</p></Empty>
        ) : transactions.length === 0 ? (
          <Empty>
            <span className="icon">💸</span>
            <p>Nenhuma movimentação em {MESES[viewMonth]}.<br />Conclua um atendimento para gerar receita.</p>
          </Empty>
        ) : (
          <AnimatePresence>
            {transactions.map((t, i) => {
              const isReceita = t.type === 'receita';
              const forma = formaConfig[t.paymentMethod];
              const date = new Date(t.date);
              const dateStr = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

              return (
                <TxCard
                  key={t._id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <TxIcon bg={isReceita ? '#d1fae5' : '#fee2e2'}>
                    {isReceita ? (forma?.icon || '💰') : '📤'}
                  </TxIcon>
                  <TxInfo>
                    <div className="desc">{t.description}</div>
                    <div className="meta">
                      {dateStr} · {t.category}
                      {forma && ` · ${forma.label}`}
                    </div>
                    {isReceita && t.status === 'pendente' && (
                      <ConfirmBtn onClick={() => {
                        setSheetData({ id: t._id, description: t.description, amount: t.amount, paymentMethod: 'pix' });
                        setSheet('confirm');
                      }}>
                        Confirmar recebimento
                      </ConfirmBtn>
                    )}
                  </TxInfo>
                  <TxRight type={t.type}>
                    <div className="amount">
                      {isReceita ? '+' : '-'} R$ {fmt(t.amount)}
                    </div>
                    <div className="status">
                      <StatusDot status={t.status} />
                      {t.status === 'recebido' ? 'Recebido' : t.status === 'pendente' ? 'Pendente' : 'Cancelado'}
                    </div>
                  </TxRight>
                </TxCard>
              );
            })}
          </AnimatePresence>
        )}
      </Section>

      {/* ── FAB ── */}
      <AddBtn
        whileTap={{ scale: 0.92 }}
        title="Registar despesa"
        onClick={() => {
          setSheetData({ description: '', amount: '', category: 'Material', date: new Date().toISOString().slice(0, 10) });
          setSheet('despesa');
        }}
      >
        +
      </AddBtn>

      {/* ── SHEETS ── */}
      <AnimatePresence>
        {sheet === 'confirm' && (
          <Overlay onClick={() => setSheet(null)}>
            <Sheet
              initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <h3>Confirmar recebimento</h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 1rem' }}>
                {sheetData.description} — <b>R$ {fmt(sheetData.amount)}</b>
              </p>
              <label>Forma de pagamento</label>
              <select value={sheetData.paymentMethod} onChange={e => setSheetData(d => ({ ...d, paymentMethod: e.target.value }))}>
                <option value="pix">⚡ PIX</option>
                <option value="dinheiro">💵 Dinheiro</option>
                <option value="cartao">💳 Cartão</option>
                <option value="transferencia">🏦 Transferência</option>
              </select>
              <SheetRow>
                <SheetCancel onClick={() => setSheet(null)}>Cancelar</SheetCancel>
                <SheetSave onClick={confirmPayment}>✓ Confirmar</SheetSave>
              </SheetRow>
            </Sheet>
          </Overlay>
        )}

        {sheet === 'despesa' && (
          <Overlay onClick={() => setSheet(null)}>
            <Sheet
              initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <h3>Registar despesa</h3>
              <label>Descrição *</label>
              <input
                placeholder="Ex: Compra de produtos"
                value={sheetData.description}
                onChange={e => setSheetData(d => ({ ...d, description: e.target.value }))}
              />
              <label>Valor (R$) *</label>
              <input
                type="number" min="0" step="0.01"
                placeholder="0,00"
                value={sheetData.amount}
                onChange={e => setSheetData(d => ({ ...d, amount: e.target.value }))}
              />
              <label>Categoria</label>
              <select value={sheetData.category} onChange={e => setSheetData(d => ({ ...d, category: e.target.value }))}>
                {['Material', 'Aluguel', 'Equipamento', 'Marketing', 'Transporte', 'Alimentação', 'Outros'].map(c =>
                  <option key={c} value={c}>{c}</option>
                )}
              </select>
              <label>Data</label>
              <input
                type="date"
                value={sheetData.date}
                onChange={e => setSheetData(d => ({ ...d, date: e.target.value }))}
              />
              <SheetRow>
                <SheetCancel onClick={() => setSheet(null)}>Cancelar</SheetCancel>
                <SheetSave onClick={addDespesa} disabled={!sheetData.description || !sheetData.amount}>
                  Registar
                </SheetSave>
              </SheetRow>
            </Sheet>
          </Overlay>
        )}
      </AnimatePresence>
    </Page>
  );
};

export default Financeiro;
